import React, { useLayoutEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { Chip, TextField, Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import Autocomplete from "@mui/material/Autocomplete";

const useStyles = makeStyles(theme => ({
  root: {
    overflowX: "scroll",
    "& > div": {
      marginRight: theme.spacing(1)
    }
  },
  label: {
    marginBottom: theme.spacing(1)
  },
  errorLabel: {
    marginBottom: theme.spacing(1),
    color: theme.palette.error.main
  },
  tags: {
    marginLeft: theme.spacing(1)
  }
}));

function Tags({
  tags,
  onDelete,
  onChange,
  label,
  disabled,
  addLabel,
  error,
  enableAutocomplete
}) {
  const classes = useStyles();
  const containerRef = useRef(null);
  const theme = useTheme();
  const [currentTags, setCurrentTags] = useState(tags);
  const ErrorMessage = () => (
    <Typography variant="body2" color="error">
      {error}
    </Typography>
  );

  useLayoutEffect(() => {
    // automatically scroll to end when new tag is ended so the user can see the tag that was just added
    if (
      tags &&
      tags.length > currentTags.length &&
      containerRef &&
      containerRef.current
    ) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
    setCurrentTags(tags);
  }, [tags]);

  if (disabled) {
    return (
      <Box display="flex" mt={2}>
        <Typography variant="subtitle1" color="primary">
          {label}
        </Typography>
        {tags &&
          tags.map((option, index) => (
            <Chip key={option} label={option} className={classes.tags} />
          ))}
      </Box>
    );
  } else if (enableAutocomplete) {
    return (
      <Box>
        <Autocomplete
          multiple
          freeSolo
          options={tags}
          className={classes.root}
          bgcolor={theme.palette.inputBackground}
          value={tags}
          disabled={disabled}
          onChange={onChange}
          renderTags={value =>
            value.map((option, index) => {
              return (
                <Box pb={0.75} pt={1.5} key={index} pr={0.5}>
                  <Chip
                    key={option}
                    label={option}
                    onDelete={() => onDelete(option)}
                  />
                </Box>
              );
            })
          }
          renderInput={params => (
            <TextField
              {...params}
              variant="filled"
              label={label}
              placeholder={addLabel}
              hiddenLabel={!label}
            />
          )}
        />
        {!!error && <ErrorMessage />}
      </Box>
    );
  } else {
    return (
      <Box display="flex" flexDirection="column" flex={1}>
        {label && (
          <Typography
            variant="caption"
            className={error ? classes.errorLabel : classes.label}
          >
            {label}
          </Typography>
        )}
        <Box
          display="flex"
          bgcolor={theme.palette.inputBackground}
          minHeight={56}
          py={0.75}
          px={1.5}
        >
          <Box display="flex" className={classes.root} ref={containerRef}>
            {tags &&
              tags.map((tag, index) => (
                <Box py={0.75} key={index}>
                  <Chip
                    disabled={disabled}
                    label={tag}
                    onDelete={() => onDelete(tag, index)}
                  />
                </Box>
              ))}
          </Box>
        </Box>
        {!!error && <ErrorMessage />}
      </Box>
    );
  }
}

export default Tags;

Tags.propTypes = {
  tags: PropTypes.array,
  onChange: PropTypes.func,
  onDelete: PropTypes.func
};

Tags.defaultProps = {
  enableAutocomplete: true
};
